import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  listPostsForNewsletter,
  getSubscriberStats,
  getNewsletterAudienceStats,
  listReportsForNewsletter,
  previewNewsletter,
  sendNewsletter,
  listSubscribers,
  addSubscriber,
  deleteSubscriber,
  getBookAnnouncementAudienceStats,
  previewBookAnnouncement,
  sendBookAnnouncement,
  backfillSubscribersAsMembers,
  getFailedNewsletterRecipients,
  resendFailedNewsletter,
} from "@/lib/newsletter.functions";



export const Route = createFileRoute("/admin/newsletter")({
  component: NewsletterPage,
});

const LS_KEY = "newsletter_sender_v4";
const EMAIL_RE = /^[^\s<>@]+@[^\s<>@]+\.[^\s<>@]+$/;

function NewsletterPage() {
  const listFn = useServerFn(listPostsForNewsletter);
  const statsFn = useServerFn(getSubscriberStats);
  const audienceFn = useServerFn(getNewsletterAudienceStats);
  const reportsFn = useServerFn(listReportsForNewsletter);
  const previewFn = useServerFn(previewNewsletter);
  const sendFn = useServerFn(sendNewsletter);

  const { data: postsData, isLoading } = useQuery({
    queryKey: ["newsletter-posts"],
    queryFn: () => listFn(),
  });
  const { data: stats } = useQuery({
    queryKey: ["newsletter-stats"],
    queryFn: () => statsFn(),
  });
  const { data: audience } = useQuery({
    queryKey: ["newsletter-audience"],
    queryFn: () => audienceFn(),
  });
  const { data: reportsData } = useQuery({
    queryKey: ["newsletter-reports"],
    queryFn: () => reportsFn(),
  });

  const [subject, setSubject] = useState("");
  const [preheader, setPreheader] = useState("");
  const [introHtml, setIntroHtml] = useState(
    "<p>Merhaba,</p><p>Bu haftaki seçkimi aşağıda paylaşıyorum.</p>"
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [includeFollowCta, setIncludeFollowCta] = useState(true);
  const [includeEkonomim, setIncludeEkonomim] = useState(false);
  const [includeCop31, setIncludeCop31] = useState(false);
  const [reportId, setReportId] = useState<string>("");
  const [audienceMode, setAudienceMode] = useState<"subscribers" | "members" | "all">("subscribers");

  const DEFAULT_SENDER_EMAIL = "bulten@akillisurdurulebilirlik.com";
  const FROM_DOMAIN = "akillisurdurulebilirlik.com";
  const DEFAULT_SENDER_NAME = "Akıllı Sürdürülebilirlik";
  const initialSender = (() => {
    if (typeof window === "undefined") return { name: DEFAULT_SENDER_NAME, email: DEFAULT_SENDER_EMAIL };
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        const trimmedEmail = typeof parsed.email === "string" ? parsed.email.trim() : "";
        const email = EMAIL_RE.test(trimmedEmail) && trimmedEmail.toLowerCase().endsWith(`@${FROM_DOMAIN}`)
          ? parsed.email.trim()
          : DEFAULT_SENDER_EMAIL;
        return { name: parsed.name || DEFAULT_SENDER_NAME, email };
      }
    } catch {}
    return { name: DEFAULT_SENDER_NAME, email: DEFAULT_SENDER_EMAIL };
  })();
  const [senderName, setSenderName] = useState<string>(initialSender.name);
  const [senderEmail, setSenderEmail] = useState<string>(initialSender.email);
  const [testEmail, setTestEmail] = useState("");

  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [result, setResult] = useState<{ sent: number; failed: number; total: number; errors: string[] } | null>(null);

  const posts = postsData?.posts ?? [];
  const orderedSelected = useMemo(
    () => selectedIds.map((id) => posts.find((p) => p.id === id)).filter(Boolean),
    [selectedIds, posts]
  );

  const toggle = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };
  const move = (id: string, dir: -1 | 1) => {
    setSelectedIds((prev) => {
      const i = prev.indexOf(id);
      if (i < 0) return prev;
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const saveSender = () => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({ name: senderName, email: DEFAULT_SENDER_EMAIL }));
    } catch {}
  };

  const previewMut = useMutation({
    mutationFn: () =>
      previewFn({
        data: {
          subject,
          preheader,
          introHtml,
          postIds: selectedIds,
          includeFollowCta,
          includeEkonomim,
          includeCop31,
          reportId: reportId || null,
        },
      }),
    onSuccess: (r) => setPreviewHtml(r.html),
  });

  const sendMut = useMutation({
    mutationFn: (opts: { test: boolean }) => {
      saveSender();
      return sendFn({
        data: {
          subject,
          preheader,
          introHtml,
          postIds: selectedIds,
          includeFollowCta,
          includeEkonomim,
          includeCop31,
          reportId: reportId || null,
          audience: audienceMode,
          senderName,
          senderEmail,
          testEmail: opts.test ? testEmail : undefined,
        },
      });
    },
    onSuccess: (r) => setResult(r),
    onError: (e: Error) => alert("Gönderim hatası: " + e.message),
  });

  const heroTitle = orderedSelected[0]
    ? ((orderedSelected[0] as any).title_tr || (orderedSelected[0] as any).title_en || "")
    : "";
  const subjectReused = Boolean(
    subject.trim() &&
      (stats?.recentSubjects ?? []).some(
        (s2) => s2.trim().toLowerCase() === subject.trim().toLowerCase()
      )
  );

  const emailValid = EMAIL_RE.test(senderEmail.trim()) && senderEmail.trim().toLowerCase().endsWith(`@${FROM_DOMAIN}`);
  const canSend = subject.trim() && !subjectReused && emailValid && senderName.trim() && selectedIds.length > 0;

  return (
    <div className="space-y-8">
      <div className="flex items-baseline justify-between">
        <h1 className="font-serif text-3xl">Bülten</h1>
        <div className="text-xs text-ink/85">
          {stats ? `${stats.count} abone` : "Aboneler yükleniyor..."}
        </div>
      </div>

      <FailedDeliveryPanel />

      <BookAnnouncementPanel />



      <section className="grid md:grid-cols-2 gap-8">
        {/* LEFT: form */}
        <div className="space-y-5">
          <div>
            <label className="block text-xs uppercase tracking-[0.18em] text-ink/85 mb-1">Konu satırı</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Her sayıda farklı olmalı — lead yazının iddiası"
              className="w-full border border-border rounded px-3 py-2 text-sm bg-paper"
            />
            <div className="flex items-center gap-3 mt-1">
              {heroTitle && (
                <button
                  type="button"
                  onClick={() => setSubject(heroTitle)}
                  className="text-[11px] underline text-ink/70 hover:text-clay"
                >
                  Ana yazının başlığını kullan
                </button>
              )}
              <span className={`text-[11px] ${subject.length > 60 ? "text-red-600" : "text-ink/60"}`}>
                {subject.length}/60 karakter
              </span>
            </div>
            {subjectReused && (
              <p className="text-[11px] text-red-600 mt-1">
                Bu konu satırı son sayılarda kullanıldı. Aynı konu satırı açılma oranını düşürür — değiştir.
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.18em] text-ink/85 mb-1">
              Önizleme metni (preheader)
            </label>
            <input
              value={preheader}
              onChange={(e) => setPreheader(e.target.value)}
              placeholder="Gelen kutusunda konu satırının yanında görünen tek cümle"
              className="w-full border border-border rounded px-3 py-2 text-sm bg-paper"
            />
            <span className={`text-[11px] ${preheader.length > 100 ? "text-red-600" : "text-ink/60"}`}>
              {preheader.length}/100 karakter — boş bırakılırsa gelen kutusunda ilk satır görünür
            </span>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.18em] text-ink/85 mb-1">Giriş yazısı (HTML)</label>
            <textarea
              value={introHtml}
              onChange={(e) => setIntroHtml(e.target.value)}
              rows={4}
              className="w-full border border-border rounded px-3 py-2 text-sm bg-paper font-mono"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] text-ink/85 mb-1">Gönderen adı</label>
              <input
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
                className="w-full border border-border rounded px-3 py-2 text-sm bg-paper"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] text-ink/85 mb-1">Gönderen e-posta *</label>
              <input
                value={senderEmail}
                onChange={(e) => setSenderEmail(e.target.value)}
                placeholder={DEFAULT_SENDER_EMAIL}
                className="w-full border border-border rounded px-3 py-2 text-sm bg-paper"
              />
            </div>
          </div>
          {!emailValid && (
            <p className="text-[11px] text-red-600 -mt-3">
              Gönderen e-postası @{FROM_DOMAIN} alan adından olmalı.
            </p>
          )}
          <p className="text-[11px] text-ink/85 -mt-3">
            Gmail gibi harici adresler gönderimde reddedilir; bülten doğrulanmış alan adından gönderilir.
          </p>


          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={includeFollowCta}
              onChange={(e) => setIncludeFollowCta(e.target.checked)}
            />
            "Diğer Yazıları Okumak İçin" butonunu ekle
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={includeEkonomim}
              onChange={(e) => setIncludeEkonomim(e.target.checked)}
            />
            Ekonomim'de Haftanın Yazısı'nı bültenin sonuna ekle
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={includeCop31}
              onChange={(e) => setIncludeCop31(e.target.checked)}
            />
            COP31 Rehberi butonunu ve yönlendirmesini ekle
          </label>

          <div>
            <label className="block text-xs uppercase tracking-[0.18em] text-ink/85 mb-1">
              Öne çıkan araştırma (opsiyonel)
            </label>
            <select
              value={reportId}
              onChange={(e) => setReportId(e.target.value)}
              className="w-full border border-border rounded px-3 py-2 text-sm bg-paper"
            >
              <option value="">— Araştırma ekleme —</option>
              {(reportsData?.reports ?? []).map((r: any) => (
                <option key={r.id} value={r.id}>
                  {r.year ? `[${r.year}] ` : ""}
                  {r.title}
                  {r.organization ? ` — ${r.organization}` : ""}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-ink/60 mt-1">
              Seçilirse, Ekonomim bloğunun altında "Öne Çıkan Araştırma" kartı olarak görünür.
            </p>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.18em] text-ink/85 mb-2">
              Alıcı kitlesi
            </label>
            <div className="space-y-1.5 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="audience"
                  value="subscribers"
                  checked={audienceMode === "subscribers"}
                  onChange={() => setAudienceMode("subscribers")}
                />
                Sadece bülten aboneleri ({audience?.subscribers ?? "…"})
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="audience"
                  value="members"
                  checked={audienceMode === "members"}
                  onChange={() => setAudienceMode("members")}
                />
                Sadece site üyeleri ({audience?.members ?? "…"})
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="audience"
                  value="all"
                  checked={audienceMode === "all"}
                  onChange={() => setAudienceMode("all")}
                />
                Hepsi — aboneler + üyeler (tekilleştirilmiş, {audience?.all ?? "…"})
              </label>
            </div>
          </div>


          <div>
            <h3 className="text-xs uppercase tracking-[0.18em] text-ink/85 mb-2">
              Seçili yazılar ({selectedIds.length})
            </h3>
            {selectedIds.length === 0 ? (
              <p className="text-xs text-ink/50">Sağdan yazı seçin.</p>
            ) : (
              <ol className="space-y-1.5">
                {orderedSelected.map((p, i) => (
                  <li key={p!.id} className="flex items-center gap-2 text-sm border border-border rounded px-2 py-1.5">
                    <span className="text-xs text-ink/50 w-5">{i + 1}.</span>
                    <span className="flex-1 truncate">{p!.title_tr || p!.title_en || p!.slug}</span>
                    <button onClick={() => move(p!.id, -1)} className="text-xs text-ink/60 hover:text-ink">↑</button>
                    <button onClick={() => move(p!.id, 1)} className="text-xs text-ink/60 hover:text-ink">↓</button>
                    <button onClick={() => toggle(p!.id)} className="text-xs text-clay">×</button>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>

        {/* RIGHT: post picker */}
        <div>
          <h3 className="text-xs uppercase tracking-[0.18em] text-ink/85 mb-2">Yayındaki yazılar</h3>
          {isLoading ? (
            <p className="text-sm text-ink/60">Yükleniyor...</p>
          ) : (
            <ul className="space-y-1 max-h-[520px] overflow-y-auto border border-border rounded p-2">
              {posts.map((p) => {
                const checked = selectedIds.includes(p.id);
                return (
                  <li key={p.id}>
                    <label className={`flex items-start gap-2 px-2 py-2 rounded cursor-pointer hover:bg-ink/5 ${checked ? "bg-clay/10" : ""}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(p.id)}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.15em] text-ink/85">{p.category}</div>
                        <div className="text-sm truncate">{p.title_tr || p.title_en || p.slug}</div>
                      </div>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      {/* Actions */}
      <section className="border-t border-border pt-6 space-y-4">
        <div className="flex flex-wrap gap-3 items-center">
          <button
            onClick={() => previewMut.mutate()}
            disabled={previewMut.isPending || selectedIds.length === 0}
            className="px-4 py-2 border border-ink/30 rounded-full text-xs uppercase tracking-[0.18em] hover:bg-ink hover:text-paper disabled:opacity-40"
          >
            {previewMut.isPending ? "..." : "Önizleme"}
          </button>

          <div className="flex items-center gap-2">
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="test@adresin.com"
              className="border border-border rounded px-3 py-1.5 text-sm bg-paper w-56"
            />
            <button
              onClick={() => sendMut.mutate({ test: true })}
              disabled={!canSend || !testEmail || sendMut.isPending}
              className="px-4 py-2 border border-ink/30 rounded-full text-xs uppercase tracking-[0.18em] hover:bg-ink hover:text-paper disabled:opacity-40"
            >
              Test gönder
            </button>
          </div>

          {(() => {
            const audienceCount =
              audienceMode === "subscribers"
                ? audience?.subscribers
                : audienceMode === "members"
                ? audience?.members
                : audience?.all;
            const audienceLabel =
              audienceMode === "subscribers"
                ? "abone"
                : audienceMode === "members"
                ? "site üyesi"
                : "alıcı (abone + üye)";
            return (
              <button
                onClick={() => {
                  if (!confirm(`Bülten ${audienceCount ?? "?"} ${audienceLabel}'ya gönderilecek. Devam?`)) return;
                  sendMut.mutate({ test: false });
                }}
                disabled={!canSend || sendMut.isPending}
                className="px-5 py-2 bg-clay text-paper rounded-full text-xs uppercase tracking-[0.22em] disabled:opacity-40 ml-auto"
              >
                {sendMut.isPending
                  ? "Gönderiliyor..."
                  : `Gönder — ${audienceLabel} (${audienceCount ?? 0})`}
              </button>
            );
          })()}
        </div>


        {result && (
          <div className="text-sm border border-border rounded p-4 bg-paper">
            <div>
              <strong>{result.sent}</strong> kuyruğa alındı · <strong>{result.failed}</strong> başarısız · toplam{" "}
              {result.total}
            </div>
            {result.errors.length > 0 && (
              <ul className="mt-2 text-xs text-clay space-y-1">
                {result.errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      {previewHtml && (
        <section>
          <h3 className="text-xs uppercase tracking-[0.18em] text-ink/85 mb-2">Önizleme</h3>
          <iframe
            srcDoc={previewHtml}
            className="w-full h-[700px] border border-border rounded bg-white"
            title="Newsletter preview"
          />
        </section>
      )}

      <SubscribersPanel />
    </div>
  );
}

function FailedDeliveryPanel() {
  const failedFn = useServerFn(getFailedNewsletterRecipients);
  const resendFn = useServerFn(resendFailedNewsletter);
  const [includeCop31, setIncludeCop31] = useState(true);
  const [result, setResult] = useState<{ sent: number; failed: number; errors: string[] } | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["newsletter-failed"],
    queryFn: () => failedFn({ data: {} }),
  });

  const resend = useMutation({
    mutationFn: (includeUnreached: boolean) =>
      resendFn({ data: { includeCop31, includeFollowCta: true, includeUnreached } }),
    onSuccess: (r) => {
      setResult(r);
      refetch();
    },
    onError: (e: Error) => alert("Yeniden gönderim hatası: " + e.message),
  });

  const failedCount = data?.failed.length ?? 0;

  return (
    <section className="border border-border rounded p-5 bg-paper/60 space-y-3">
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="font-serif text-xl">Başarısız teslimler</h2>
        <span className="text-xs text-ink/70">
          {isLoading
            ? "Kontrol ediliyor..."
            : data?.campaignId
              ? `Son kampanya · ${data.sent}/${data.total} teslim edildi`
              : "Kampanya bulunamadı"}
        </span>
      </div>

      {!isLoading && failedCount === 0 && (
        <p className="text-sm text-ink/80">Son bültende yeniden gönderilmesi gereken alıcı yok.</p>
      )}

      {failedCount > 0 && (
        <>
          <p className="text-sm text-ink/85">
            <strong>{failedCount} alıcı</strong> bülteni alamadı (kuyrukta zaman aşımı / hata). Aynı bülteni bu
            adreslere yeniden kuyruğa alabilirsin.
          </p>
          <details className="text-xs text-ink/70">
            <summary className="cursor-pointer">Adresleri göster</summary>
            <div className="mt-2 max-h-40 overflow-auto font-mono leading-5">
              {data?.failed.map((e) => (
                <div key={e}>{e}</div>
              ))}
            </div>
          </details>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={includeCop31}
              onChange={(e) => setIncludeCop31(e.target.checked)}
            />
            COP31 Rehberi bloğunu dahil et
          </label>
          <button
            onClick={() => resend.mutate(false)}
            disabled={resend.isPending}
            className="px-4 py-2 text-sm rounded bg-ink text-paper disabled:opacity-50"
          >
            {resend.isPending ? "Kuyruğa alınıyor..." : `Başarısızları yeniden gönder (${failedCount})`}
          </button>
        </>
      )}

      {(data?.unreached ?? 0) > 0 && (
        <div className="space-y-2 border-t border-border pt-3">
          <p className="text-sm text-ink/85">
            <strong>{data?.unreached} üye</strong> bu bülteni henüz hiç almadı. Daha önce alanlara tekrar
            gitmez; saatlik limit dolarsa kalanlar için bir saat sonra yeniden bas.
          </p>
          <button
            onClick={() => resend.mutate(true)}
            disabled={resend.isPending}
            className="px-4 py-2 text-sm rounded bg-clay text-paper disabled:opacity-50"
          >
            {resend.isPending ? "Gönderiliyor..." : `Henüz almayan üyelere gönder (${data?.unreached})`}
          </button>
        </div>
      )}

      {result && (
        <div className="text-sm text-ink/85">
          Kuyruğa alınan: {result.sent} · Hata: {result.failed}
          {result.errors.length > 0 && (
            <ul className="mt-1 list-disc pl-5 text-xs text-ink/70">
              {result.errors.slice(0, 10).map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}


function BookAnnouncementPanel() {
  const statsFn = useServerFn(getBookAnnouncementAudienceStats);
  const previewFn = useServerFn(previewBookAnnouncement);
  const sendFn = useServerFn(sendBookAnnouncement);
  const backfillFn = useServerFn(backfillSubscribersAsMembers);

  const { data: stats, refetch: refetchStats } = useQuery({
    queryKey: ["book-announcement-audience"],
    queryFn: () => statsFn(),
  });

  const backfillMut = useMutation({
    mutationFn: () => backfillFn(),
    onSuccess: (r) => {
      alert(
        `Backfill tamamlandı.\nOluşturulan üye: ${r.created}\nBaşarısız: ${r.failed}\nEksik abone sayısı: ${r.totalMissing}` +
          (r.errors.length ? `\n\nİlk hatalar:\n${r.errors.join("\n")}` : "")
      );
      refetchStats();
    },
    onError: (e: Error) => alert("Backfill hatası: " + e.message),
  });

  const BOOK_OPTIONS = {
    bosluktur: {
      label: "Hayatı Yaşanır Kılan, Boşluktur (2026)",
      defaultSubject: "Yeni kitap: Hayatı Yaşanır Kılan, Boşluktur",
    },
    "yalnizlik-as": {
      label: "Yalnızlık A.Ş. (üyelere özel PDF)",
      defaultSubject: "Yeni kitap: Yalnızlık A.Ş. — Üyelere özel ücretsiz PDF",
    },
  } as const;
  type BookKey = keyof typeof BOOK_OPTIONS;

  const [book, setBook] = useState<BookKey>("bosluktur");
  const [subject, setSubject] = useState<string>(BOOK_OPTIONS.bosluktur.defaultSubject);
  const [senderName, setSenderName] = useState("Arda Öztaşkın");
  const [testEmail, setTestEmail] = useState("");
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [result, setResult] = useState<{ sent: number; failed: number; total: number; errors: string[] } | null>(null);

  const previewMut = useMutation({
    mutationFn: () => previewFn({ data: { subject, book } }),
    onSuccess: (r) => setPreviewHtml(r.html),
  });

  const sendMut = useMutation({
    mutationFn: (opts: { test: boolean }) =>
      sendFn({
        data: {
          subject,
          senderName,
          book,
          testEmail: opts.test ? testEmail : undefined,
        },
      }),
    onSuccess: (r) => setResult(r),
    onError: (e: Error) => alert("Gönderim hatası: " + e.message),
  });

  const totalAudience = stats?.count ?? 0;

  return (
    <section className="border border-clay/40 bg-clay/5 rounded p-5 md:p-6 space-y-4">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-serif text-2xl">Kitap Duyurusu</h2>
          <p className="text-xs text-ink/70 mt-1">
            Tek konulu özel bülten: seçilen kitabın tanıtımı tüm site üyelerine gider.
          </p>
        </div>
        <div className="text-xs text-ink/85 text-right space-y-2">
          {stats ? (
            <div><strong>{stats.count}</strong> site üyesi</div>
          ) : (
            "Alıcılar yükleniyor…"
          )}
          <button
            onClick={() => {
              if (!confirm("Henüz site üyesi olmayan tüm bülten aboneleri için üyelik oluşturulacak. Devam?")) return;
              backfillMut.mutate();
            }}
            disabled={backfillMut.isPending}
            className="px-3 py-1.5 border border-ink/30 rounded-full text-[10px] uppercase tracking-[0.18em] hover:bg-ink hover:text-paper disabled:opacity-40"
          >
            {backfillMut.isPending ? "..." : "Aboneleri üye yap"}
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs uppercase tracking-[0.18em] text-ink/85 mb-1">Kitap</label>
          <select
            value={book}
            onChange={(e) => {
              const key = e.target.value as BookKey;
              setBook(key);
              setSubject(BOOK_OPTIONS[key].defaultSubject);
              setPreviewHtml(null);
              setResult(null);
            }}
            className="w-full border border-border rounded px-3 py-2 text-sm bg-paper"
          >
            {(Object.keys(BOOK_OPTIONS) as BookKey[]).map((key) => (
              <option key={key} value={key}>
                {BOOK_OPTIONS[key].label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.18em] text-ink/85 mb-1">Konu satırı</label>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full border border-border rounded px-3 py-2 text-sm bg-paper"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.18em] text-ink/85 mb-1">Gönderen adı</label>
          <input
            value={senderName}
            onChange={(e) => setSenderName(e.target.value)}
            className="w-full border border-border rounded px-3 py-2 text-sm bg-paper"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <button
          onClick={() => previewMut.mutate()}
          disabled={previewMut.isPending}
          className="px-4 py-2 border border-ink/30 rounded-full text-xs uppercase tracking-[0.18em] hover:bg-ink hover:text-paper disabled:opacity-40"
        >
          {previewMut.isPending ? "..." : "Önizleme"}
        </button>

        <div className="flex items-center gap-2">
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="test@adresin.com"
            className="border border-border rounded px-3 py-1.5 text-sm bg-paper w-56"
          />
          <button
            onClick={() => sendMut.mutate({ test: true })}
            disabled={!testEmail || sendMut.isPending}
            className="px-4 py-2 border border-ink/30 rounded-full text-xs uppercase tracking-[0.18em] hover:bg-ink hover:text-paper disabled:opacity-40"
          >
            Test gönder
          </button>
        </div>

        <button
          onClick={() => {
            if (!confirm(`Kitap duyurusu ${totalAudience} alıcıya gönderilecek. Devam?`)) return;
            sendMut.mutate({ test: false });
          }}
          disabled={!subject.trim() || !senderName.trim() || sendMut.isPending || totalAudience === 0}
          className="px-5 py-2 bg-clay text-paper rounded-full text-xs uppercase tracking-[0.22em] disabled:opacity-40 ml-auto"
        >
          {sendMut.isPending ? "Gönderiliyor..." : `Tüm alıcılara gönder (${totalAudience})`}
        </button>
      </div>

      {result && (
        <div className="text-sm border border-border rounded p-4 bg-paper">
          <div>
            <strong>{result.sent}</strong> kuyruğa alındı · <strong>{result.failed}</strong> başarısız · toplam{" "}
            {result.total}
          </div>
          {result.errors.length > 0 && (
            <ul className="mt-2 text-xs text-clay space-y-1">
              {result.errors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {previewHtml && (
        <div>
          <h3 className="text-xs uppercase tracking-[0.18em] text-ink/85 mb-2">Önizleme</h3>
          <iframe
            srcDoc={previewHtml}
            className="w-full h-[600px] border border-border rounded bg-white"
            title="Book announcement preview"
          />
        </div>
      )}
    </section>
  );
}

function SubscribersPanel() {

  const listFn = useServerFn(listSubscribers);
  const addFn = useServerFn(addSubscriber);
  const delFn = useServerFn(deleteSubscriber);

  const { data, refetch, isLoading } = useQuery({
    queryKey: ["newsletter-subscribers"],
    queryFn: () => listFn(),
  });

  const [newEmail, setNewEmail] = useState("");
  const [newLang, setNewLang] = useState<"tr" | "en">("tr");
  const [msg, setMsg] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  const addMut = useMutation({
    mutationFn: async () => {
      const email = newEmail.trim().toLowerCase();
      if (!EMAIL_RE.test(email)) throw new Error("Geçersiz e-posta");
      return addFn({ data: { email, lang: newLang } });
    },
    onSuccess: (res: any) => {
      if (res?.already) setMsg("Bu adres zaten kayıtlı.");
      else {
        setMsg("Eklendi.");
        setNewEmail("");
      }
      refetch();
    },
    onError: (e: any) => setMsg(e.message || "Hata"),
  });

  const delMut = useMutation({
    mutationFn: async (id: string) => delFn({ data: { id } }),
    onSuccess: () => refetch(),
  });

  const [sourceFilter, setSourceFilter] = useState<"all" | "subscriber" | "member">("all");
  const [langFilter, setLangFilter] = useState<"all" | "tr" | "en">("all");

  const allSubs = (data?.subscribers ?? []) as Array<{
    id: string;
    email: string;
    lang: "tr" | "en";
    subscribed_at: string;
    source: "subscriber" | "member";
  }>;

  const subs = allSubs.filter((s) => {
    if (filter && !s.email.toLowerCase().includes(filter.toLowerCase())) return false;
    if (sourceFilter !== "all" && s.source !== sourceFilter) return false;
    if (langFilter !== "all" && s.lang !== langFilter) return false;
    return true;
  });

  const totalCount = allSubs.length;
  const subscriberCount = allSubs.filter((s) => s.source === "subscriber").length;
  const memberCount = allSubs.filter((s) => s.source === "member").length;
  const trCount = allSubs.filter((s) => s.lang === "tr").length;
  const enCount = allSubs.filter((s) => s.lang === "en").length;

  return (
    <section className="border-t border-border pt-8 space-y-4">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <h2 className="font-serif text-2xl">Aboneler ve üyeler</h2>
        <div className="text-xs text-ink/85">
          Toplam {totalCount} · Bülten aboneleri {subscriberCount} · Site üyeleri {memberCount} · TR {trCount} · EN {enCount}
        </div>
      </div>

      <div className="grid md:grid-cols-[1fr_120px_auto] gap-2 items-end">
        <div>
          <label className="block text-xs uppercase tracking-[0.18em] text-ink/85 mb-1">E-posta</label>
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="ornek@domain.com"
            className="w-full border border-border rounded px-3 py-2 text-sm bg-paper"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.18em] text-ink/85 mb-1">Dil</label>
          <select
            value={newLang}
            onChange={(e) => setNewLang(e.target.value as "tr" | "en")}
            className="w-full border border-border rounded px-3 py-2 text-sm bg-paper"
          >
            <option value="tr">TR</option>
            <option value="en">EN</option>
          </select>
        </div>
        <button
          type="button"
          onClick={() => {
            setMsg(null);
            addMut.mutate();
          }}
          disabled={addMut.isPending}
          className="bg-ink text-paper text-xs uppercase tracking-[0.18em] px-4 py-2 rounded disabled:opacity-60"
        >
          {addMut.isPending ? "Ekleniyor..." : "Manuel ekle"}
        </button>
      </div>
      {msg && <p className="text-xs text-ink/70">{msg}</p>}

      <div className="space-y-3">
        <div className="flex flex-wrap gap-2 items-center">
          <input
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filtrele (e-posta)"
            className="w-full md:w-72 border border-border rounded px-3 py-2 text-sm bg-paper"
          />
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value as any)}
            className="border border-border rounded px-3 py-2 text-sm bg-paper"
          >
            <option value="all">Tüm kaynaklar</option>
            <option value="subscriber">Bülten aboneleri</option>
            <option value="member">Site üyeleri</option>
          </select>
          <select
            value={langFilter}
            onChange={(e) => setLangFilter(e.target.value as any)}
            className="border border-border rounded px-3 py-2 text-sm bg-paper"
          >
            <option value="all">Tüm diller</option>
            <option value="tr">TR</option>
            <option value="en">EN</option>
          </select>
          <span className="text-xs text-ink/60">{subs.length} kayıt gösteriliyor</span>
        </div>
        <div className="border border-border rounded max-h-[480px] overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink/5 sticky top-0">
              <tr className="text-left">
                <th className="px-3 py-2 font-medium">E-posta</th>
                <th className="px-3 py-2 font-medium w-24">Kaynak</th>
                <th className="px-3 py-2 font-medium w-16">Dil</th>
                <th className="px-3 py-2 font-medium w-40">Kayıt</th>
                <th className="px-3 py-2 w-16"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={5} className="px-3 py-4 text-ink/60">Yükleniyor...</td></tr>
              ) : subs.length === 0 ? (
                <tr><td colSpan={5} className="px-3 py-4 text-ink/60">Kayıt yok.</td></tr>
              ) : (
                subs.map((s) => (
                  <tr key={s.id} className="border-t border-border">
                    <td className="px-3 py-2 break-all">{s.email}</td>
                    <td className="px-3 py-2 text-xs uppercase tracking-wider text-ink/70">
                      {s.source === "subscriber" ? "Bülten" : "Üye"}
                    </td>
                    <td className="px-3 py-2 uppercase text-ink/85">{s.lang}</td>
                    <td className="px-3 py-2 text-ink/85">
                      {new Date(s.subscribed_at).toLocaleDateString("tr-TR")}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {s.source === "subscriber" ? (
                        <button
                          type="button"
                          onClick={() => {
                            if (confirm(`${s.email} silinsin mi?`)) delMut.mutate(s.id);
                          }}
                          className="text-xs text-clay hover:underline"
                        >
                          Sil
                        </button>
                      ) : (
                        <span className="text-xs text-ink/40">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
